import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Download } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, money } from "@/lib/api";

const METRICS = [
  { key: "revenue", label: "Pendapatan", color: "#0d9488", invert: false },
  { key: "profit", label: "Laba Bersih", color: "#16a34a", invert: false },
  { key: "cost", label: "Beban", color: "#dc2626", invert: true },
  { key: "sales", label: "Penjualan (Invoice)", color: "#2563eb", invert: false },
  { key: "purchases", label: "Pembelian", color: "#7c3aed", invert: true },
  { key: "expenses", label: "Biaya", color: "#e5a139", invert: true },
  { key: "cash_in", label: "Kas Masuk", color: "#0891b2", invert: false },
  { key: "cash_out", label: "Kas Keluar", color: "#db2777", invert: true },
];
const METRIC_MAP = Object.fromEntries(METRICS.map((m) => [m.key, m]));
const PERIODS = [
  { label: "7 Hari Terakhir", days: 7 },
  { label: "30 Hari Terakhir", days: 30 },
  { label: "3 Bulan Terakhir", days: 90 },
  { label: "6 Bulan Terakhir", days: 180 },
  { label: "12 Bulan Terakhir", days: 365 },
];
const NOW_YEAR = new Date().getFullYear();
const YEARS = [NOW_YEAR, NOW_YEAR - 1, NOW_YEAR - 2, NOW_YEAR - 3];

const compact = (n) => {
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)} M`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)} jt`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)} rb`;
  return `${n}`;
};

export const AnalyticsBoard = () => {
  const [mode, setMode] = useState("days");
  const [days, setDays] = useState(30);
  const [year, setYear] = useState(NOW_YEAR);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(["revenue"]);
  useEffect(() => {
    let live = true;
    const q = mode === "year" ? `year=${year}` : `days=${days}`;
    api(`/dashboard/analytics?${q}`).then((d) => live && setData(d)).catch(() => {});
    return () => { live = false; };
  }, [mode, days, year]);
  const compare = data?.period?.compare;
  const compareLabel = data?.period?.compare_label;
  const deltaLabel = compare ? `vs ${compareLabel}` : "vs periode sebelumnya";
  const toggle = (k) => setSelected((s) => (s.includes(k) ? (s.length > 1 ? s.filter((x) => x !== k) : s) : s.length >= 4 ? s : [...s, k]));
  const delta = (k) => {
    const cur = data?.totals?.[k] || 0;
    const prev = data?.prev?.[k] || 0;
    if (prev <= 0) return cur > 0 ? 100 : 0;
    return ((cur - prev) / prev) * 100;
  };
  const downloadCsv = () => {
    if (!data) return;
    const cols = compare ? METRICS.flatMap((m) => [m.key, `${m.key}_prev`]) : METRICS.map((m) => m.key);
    const head = ["Periode", ...(compare ? METRICS.flatMap((m) => [m.label, `${m.label} (${compareLabel})`]) : METRICS.map((m) => m.label))];
    const rows = data.buckets.map((b) => [b.label, ...cols.map((c) => b[c] ?? 0)]);
    const csv = [head, ...rows].map((r) => r.join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = `analitik-${mode === "year" ? `tahun-${year}` : `${days}hari`}.csv`;
    a.click();
  };
  return (
    <section className="surface analytics-board" data-testid="analytics-board">
      <div className="ab-toolbar">
        <div>
          <h3>Kriteria Utama</h3>
          <p className="muted">Berbasis Buku Besar — mencakup semua transaksi (invoice, biaya & Jurnal Umum). Klik kartu untuk tampilkan di grafik (maks. 4).</p>
        </div>
        <div className="ab-controls">
          <div className="ab-mode" data-testid="analytics-mode-toggle">
            <button data-testid="analytics-mode-days" className={mode === "days" ? "on" : ""} onClick={() => setMode("days")}>Periode</button>
            <button data-testid="analytics-mode-year" className={mode === "year" ? "on" : ""} onClick={() => setMode("year")}>Tahunan</button>
          </div>
          {mode === "days" ? (
            <label className="ab-period">
              <span>Periode Data</span>
              <select data-testid="analytics-period-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                {PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
              </select>
            </label>
          ) : (
            <label className="ab-period">
              <span>Pilih Tahun</span>
              <select data-testid="analytics-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
          <button data-testid="analytics-download" className="secondary-btn small nowrap" onClick={downloadCsv}><Download size={13} /> Download Data</button>
        </div>
      </div>
      <div className="ab-cards">
        {METRICS.map((m) => {
          const on = selected.includes(m.key);
          const d = delta(m.key);
          const good = m.invert ? d <= 0 : d >= 0;
          return (
            <button key={m.key} data-testid={`metric-card-${m.key}`} className={`ab-card ${on ? "on" : ""}`} style={on ? { "--mc": m.color } : undefined} onClick={() => toggle(m.key)}>
              <span className="ab-card-label">{m.label}<i className="ab-count">{data?.counts?.[m.key] || 0} transaksi</i></span>
              <strong>{money(data?.totals?.[m.key] || 0)}</strong>
              <small className={good ? "good" : "bad"}>
                {d >= 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />} {Math.abs(d).toFixed(1)}% <em>{deltaLabel}</em>
              </small>
            </button>
          );
        })}
      </div>
      <div className="ab-chart" data-testid="analytics-chart">
        <ResponsiveContainer width="100%" height={270}>
          <LineChart data={data?.buckets || []} margin={{ top: 10, right: 14, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 6" vertical={false} stroke="#e8edf1" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8b99aa" }} tickLine={false} axisLine={{ stroke: "#e6ebf0" }} interval="preserveStartEnd" minTickGap={26} />
            <YAxis tick={{ fontSize: 10, fill: "#8b99aa" }} tickLine={false} axisLine={false} tickFormatter={compact} width={54} />
            <Tooltip
              formatter={(v, name) => {
                const base = name.replace(/_prev$/, "");
                const lbl = METRIC_MAP[base]?.label || base;
                return [money(v), name.endsWith("_prev") ? `${lbl} (${compareLabel})` : lbl];
              }}
              labelStyle={{ fontSize: 11, fontWeight: 700, color: "#12283a" }}
              contentStyle={{ borderRadius: 10, border: "1px solid #e3e9ee", boxShadow: "0 10px 26px #12283a1f", fontSize: 11 }} />
            {METRICS.filter((m) => selected.includes(m.key)).map((m) => (
              <Line key={m.key} type="monotone" dataKey={m.key} name={m.key} stroke={m.color} strokeWidth={2.2} dot={{ r: 2.5, strokeWidth: 0, fill: m.color }} activeDot={{ r: 4.5 }} />
            ))}
            {compare && METRICS.filter((m) => selected.includes(m.key)).map((m) => (
              <Line key={`${m.key}_prev`} type="monotone" dataKey={`${m.key}_prev`} name={`${m.key}_prev`} stroke={m.color} strokeOpacity={0.45} strokeWidth={1.6} strokeDasharray="5 4" dot={false} activeDot={{ r: 3.5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {compare && <p className="ab-compare-note">Garis tegas = {year} · garis putus-putus = {compareLabel}</p>}
      </div>
    </section>
  );
};
